// ============================================================
// Designation Modal Component
// ============================================================

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { X, Tag } from 'lucide-react';

export function DesignationModal({
  open,
  onClose,
  designation = null,
  departments = [],
  onSubmit,
  loading = false,
}) {
  const [name, setName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (designation) {
      setName(designation.name || '');
      setDepartmentId(designation.department_id || designation.departmentId || '');
      setDescription(designation.description || '');
    } else {
      setName('');
      setDepartmentId('');
      setDescription('');
    }
    setErrors({});
  }, [designation, open]);

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = 'Designation name is required';
    }
    if (!departmentId) {
      newErrors.departmentId = 'Department is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      name: name.trim(),
      department_id: departmentId,
      description: description.trim(),
    });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Tag size={20} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {designation ? 'Edit Designation' : 'Create New Designation'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small">
          <X size={20} />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Designation Name"
            placeholder="e.g. Senior Science Teacher"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            disabled={loading}
          />

          <FormControl fullWidth required error={!!errors.departmentId} disabled={loading}>
            <InputLabel id="designation-department-label">Department</InputLabel>
            <Select
              labelId="designation-department-label"
              value={departmentId}
              label="Department"
              onChange={(e) => setDepartmentId(e.target.value)}
            >
              {(departments || []).map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
            {errors.departmentId && <FormHelperText>{errors.departmentId}</FormHelperText>}
          </FormControl>

          <TextField
            label="Description"
            placeholder="e.g. Leads high school science curriculum..."
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', gap: 1 }}>
          <Button onClick={onClose} disabled={loading} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {designation ? 'Save Changes' : 'Create Designation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
